import { useTheme } from '@mui/material';
import { BarChart as MuiBarChart } from '@mui/x-charts';
import { useIntl } from 'react-intl';

import { useWindowSize } from '@verantos-dev/shared-hooks';
import {
  CohortCharacteristicsResultType,
  CohortSummaryAttributeResult,
} from '@verantos-dev/types-cohorts';

import { calculateAxisData, generateRangeArray } from '../helpers';

const CARD_HEIGHT = 450;

type BarChartProps = {
  data: CohortSummaryAttributeResult[] | undefined;
  resultType: CohortCharacteristicsResultType;
};

export const BarChart = ({ data, resultType }: BarChartProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const { width: windowWidth } = useWindowSize();

  if (!data) return null;

  // generating range for xAxis labels of cohort characteristics age chart
  const barSteps = generateRangeArray(0, 100, 5);

  // mapping data with xAxis steps
  const axisData = calculateAxisData(barSteps, data);

  const windowWidthToUse = windowWidth < 1440 ? 1366 : windowWidth > 1920 ? 1920 : windowWidth;
  const widthToUse = windowWidthToUse - 200;

  return (
    <MuiBarChart
      data-testid={`cohortCharacteristic-graph-bar-chart-${resultType}`}
      height={CARD_HEIGHT}
      width={widthToUse}
      margin={{ left: 105, right: 50, top: 20, bottom: 70 }}
      dataset={axisData}
      series={[
        {
          label: (location) =>
            location === 'tooltip'
              ? intl.formatMessage({
                  id: 'projects.cohortBuilder.cohortCharacteristicsCharts.barChart.seriesLabel',
                })
              : '',
          dataKey: 'attributeCount',
          color: theme.palette.tertiary.darkBlue,
          valueFormatter: (item, context) => {
            const attributeName = axisData[context.dataIndex].attributeName;
            const attributeCount = axisData[context.dataIndex].attributeCount.toLocaleString();
            const attributePercentage = axisData[context.dataIndex].attributePercentage || 0;

            return `${attributeName}\u00A0\u00A0${attributeCount}  (${attributePercentage}%)`;
          },
        },
      ]}
      slotProps={{
        legend: { hidden: true },
        popper: {
          sx: {
            '& .MuiChartsTooltip-labelCell': {
              backgroundColor: `${theme.palette.white.main} !important`,
              '& .MuiTypography-root': {
                color: `${theme.palette.text.secondary}  !important`,
              },
            },
            '& .MuiChartsTooltip-valueCell': {
              backgroundColor: `${theme.palette.white.main} !important`,
              paddingLeft: '5px !important',
              '& .MuiTypography-root': {
                color: `${theme.palette.text.primary}  !important`,
              },
            },
          },
        },
      }}
      xAxis={[
        {
          label: intl.formatMessage({
            id: 'projects.cohortBuilder.cohortCharacteristicsCharts.barChart.xAxisLabel',
          }),
          labelStyle: { fontWeight: 500 },
          scaleType: 'band', // Use band scale for categorical data
          tickLabelInterval: (value, index) => true,
          dataKey: 'attributeName', // Use dataKey to define the key in the data object
          valueFormatter: (ageRange, context) => {
            const startRange = ageRange.split('-')[0];
            return startRange;
          },
          tickPlacement: 'start',
          tickLabelPlacement: 'tick',
          hideTooltip: true,
        },
      ]}
      yAxis={[
        {
          label: intl.formatMessage({
            id: 'projects.cohortBuilder.cohortCharacteristicsCharts.barChart.yAxisLabel',
          }),
          dataKey: 'attributeCount',
          labelStyle: { transform: 'rotate(0deg) translateX(-30px)', fontWeight: 500 },
          hideTooltip: true,
        },
      ]}
    />
  );
};
